<template>
    <b-modal :id="modalId" :title="title" size="lg" hide-footer @show="show">
        <b-row>
            <b-col cols="6" v-if="formType != formTypeEnum.NewRxRequest">
                <b>FROM: </b><span>{{ store.name }}</span><br>
                <span>{{ store.fullAddress }}</span><br>
            </b-col>

            <b-col cols="12" v-else>
                <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate required />
            </b-col>
            <b-col cols="12">
                <SearchComponent v-if="formType == formTypeEnum.NewRxRequest" v-model="selectedPrescriber"
                    label="Doctor" search-u-r-l="/Prescriber" required
                    :static-options="patientPrescribers"
                    :results-modifier="prescriberResultsModifier" />
                <b-badge v-if="showInvalidFormAlert" variant="danger">
                    <i class="fas fa-exclamation-triangle"></i> Prescriber doesn't have any fax number
                </b-badge>
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="6">
                <b>PATIENT: </b><span>{{ patient.displayNamesForPerson() }}</span><br>
                <span>{{ patient.fullAddress }}</span><br>
                <b>PATIENT DOB: </b><span>{{ patient.dateOfBirth | formatLocalDate('L') }}</span>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="4">
                <b>PHONE: </b><span>{{ patient.phoneNumber | formatPhone }}</span><br>
            </b-col>
            <b-col cols="4">
                <b>CELL PHONE: </b>{{ patient.cellNumber | formatPhone }}<br>
            </b-col>
        </b-row>
        <hr>
        <b-row>
            <b-col cols="12">
                <SearchComponent v-if="formType == formTypeEnum.NewRxRequest" v-model="selectedDrug" label="Drug"
                    search-u-r-l="/Drug" required />
                <b v-else>PRESCRIBED: </b><span>{{ drugToShow.productNameLong }}</span>
            </b-col>
            <b-col cols="12" v-if="formType == formTypeEnum.NewRxRequest">
                <SearchComponent v-model="selectedProgram" label="Program" search-u-r-l="/Program" prepopulate clearable />
            </b-col>
        </b-row>

        <PrescriptionDetailsSummary v-if="prescription && formType != formTypeEnum.NewRxRequest"
            v-model="prescription" />
        <hr>

        <b-row>
            <b-col>
                <fg-input label="PHARMACY COMMENTS">
                    <AutoCompleteTextarea v-model="faxFormData.note" class="textarea-autosize" block-listener placeholder="Write here any comments" />
                </fg-input>
            </b-col>
        </b-row>

        <b-row v-if="formType === formTypeEnum.RxClarification">
            <b-col>
                <b-check v-model="appendPrescriptionImage">
                    Append prescription image to fax
                </b-check>
            </b-col>
        </b-row>

        <b-row v-if="prescriberToShow && prescriberToShow.altFaxNumber">
            <b-col>
                <b-check v-model="useAltFax">
                    Use Alternate Fax Number ({{ prescriberToShow.altFaxNumber }})
                </b-check>
            </b-col>
        </b-row>

        <hr>

        <b-row v-if="isSending">
            <b-spinner label="Spinning" class="mr-2" />
            <strong>Sending...</strong>
        </b-row>
        <b-row v-else>
            <b-col>
                <b-button class="mt-3" block @click="close">
                    Cancel
                </b-button>
            </b-col>
            <b-col>
                <b-button class="mt-3" variant="outline-primary" block @click="preview" :disabled="!canPreview">
                    Preview
                </b-button>
            </b-col>
            <b-col>
                <b-button class="mt-3" variant="success" block @click="send" :disabled="!isFormValid">
                    Send
                </b-button>
            </b-col>
        </b-row>

        <FaxPreviewModal ref="previewModal" :modal-id="`${modalId}-preview`" />
    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
import { Store } from '@/models/Store';
import { Prescriber, PrescriberCommunicationPreference } from '@/models/Prescriber';
import { Patient } from '@/models/Patient';
import { FaxForm, Prescription } from '@/models/Prescription';
import { PatientDocument } from "@/models/PatientDocument";
import { FaxType, PrescriberFollowUpCase } from "@/models/PrescriberFollowUpCase";
import { Drug, DrugProgram } from "@/models/Drug/Drug";
import { Program } from "@/models/Program";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PrescriptionDetailsSummary from '@/components/Prescription/PrescriptionDetailsSummary.vue'
import SearchComponent from '@/components/SearchComponent.vue';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';
import { PrescriberContactTemplate } from "@/models/FaxTemplates";
import { getUserSettingParsed } from "@/services/UserSettingsService";
import FaxPreviewModal from "@/components/Fax/FaxPreviewModal.vue";

@Component({
    name: "FaxToPhysicianFormModal",
    components: { PrescriptionDetailsSummary, SearchComponent, AutoCompleteTextarea, FaxPreviewModal }
})
export default class FaxToPhysicianFormModal extends Vue {
    @Prop({ default: () => new Store() }) private store!: Store;
    @Prop({ default: () => new Prescriber() }) private prescriber!: Prescriber;
    @Prop() private prescription!: Prescription;
    @Prop() private patient!: Patient;
    @Prop({ default: () => new Drug() }) private drug!: Drug;
    @Prop({ default: "" }) private modalId!: string;
    @Prop({ default: FaxType.RefillAuthorization }) private formType!: FaxType;
    @Prop({ default: null }) private customTemplate!: PrescriberContactTemplate | null;

    @Ref("previewModal") private previewModal!: FaxPreviewModal;

    private faxFormData: FaxForm = new FaxForm();
    private isSending: boolean = false;

    private selectedPrescriber: Prescriber = new Prescriber();
    private selectedDrug: Drug = new Drug();
    private patientPrescribers: Prescriber[] = [];

    protected formTypeEnum = FaxType;

    private storeFilter: Store = new Store();
    private excludeRefillRequestsFromNotes: boolean = false;
    private selectedProgram: Program = new Program();
    private loadingPrograms: boolean = false;

    protected appendPrescriptionImage = false;
    protected useAltFax = false;

    created() {
        this.getConfig();
    }

    @Watch('selectedDrug')
    async onSelectedDrugChange(drug: Drug) {
        this.selectedProgram = new Program();

        if (this.formType !== FaxType.NewRxRequest || !drug?.drugId) return;

        try {
            this.loadingPrograms = true;
            const res = await this.$http.get<DrugProgram[]>(`/Drug/${drug.drugId}/${drug.source}/Program`);
            const programs = res.data ?? [];

            if (programs.length === 1 && programs[0].program) {
                this.selectedProgram = new Program(programs[0].program);
            }
        } catch (err) {
            console.error("Failed to load drug programs:", err);
        } finally {
            this.loadingPrograms = false;
        }
    }

    get lastStore(): any {
        return getUserSettingParsed<any>('printQueue_lastStore', null);
    }

    get title(): string {
        if (this.customTemplate) return this.customTemplate.name;

        if (this.formType == FaxType.RefillAuthorization) return 'Refill Authorization Request';
        if (this.formType == FaxType.RxClarification) return 'RX Clarification Request';
        if (this.formType == FaxType.NewRxRequest) return 'New RX Request';
        if (this.formType == FaxType.AlternateRxRequest) return 'Alternate RX Request';

        return '';
    }

    get endpointURI(): string {
        if (this.customTemplate) return `custom-prescriber-fax-send/${this.customTemplate.id}`;

        if (this.formType == FaxType.RefillAuthorization) return 'request-refill-authorization';
        if (this.formType == FaxType.RxClarification) return 'request-rx-clarification';
        if (this.formType == FaxType.NewRxRequest) return 'request-new-rx';
        return "";
    }

    get prescriberToShow(): Prescriber {
        return this.prescriber?.id ? this.prescriber : this.selectedPrescriber;
    }

    get drugToShow(): Drug {
        return this.drug?.packageID ? this.drug : this.selectedDrug;
    }

    show() {
        this.clearForm();
        this.fetchPatientPrescribers();
    }

    async fetchPatientPrescribers() {
        const patientId = this.patient?.id;
        if (!patientId) {
            this.patientPrescribers = [];
            return;
        }
        try {
            const response = await this.$http.get<Prescriber[]>(`/Prescriber/by-patient/${patientId}`);
            this.patientPrescribers = response.data || [];
        } catch {
            this.patientPrescribers = [];
        }
    }

    prescriberResultsModifier(searchResults: any[], params?: { searchTerm?: string }) {
        const searchTerm = (params?.searchTerm ?? "").trim();
        // While the user is searching, show only API matches — recent prescribers
        // would dilute the results and led to incorrect selections (SD-3952).
        if (searchTerm) return searchResults;

        if (!this.patientPrescribers.length) return searchResults;

        return [
            { heading: true, label: "Patient's Prescribers" },
            ...this.patientPrescribers,
        ];
    }

    // Populates faxFormData from the current form state. Shared by send and preview so a preview
    // is generated from exactly the same payload that would be transmitted.
    private populateFaxFormData() {
        this.faxFormData.fax = this.useAltFax && this.prescriberToShow.altFaxNumber ?
            this.prescriberToShow.altFaxNumber : this.prescriberToShow.faxNumber;

        if (this.faxFormData.verbalTransfer == null) {
            this.faxFormData.verbalTransfer = false;
        }

        if (this.formType == FaxType.NewRxRequest) {
            this.faxFormData.prescriberID = Number(this.selectedPrescriber.id);
            this.faxFormData.patientID = Number(this.patient.id);
            this.faxFormData.packageID = Number(this.selectedDrug.drugId);
            this.faxFormData.drugSource = Number(this.selectedDrug.source);
            this.faxFormData.storeID = Number(this.storeFilter.id);
            if (this.selectedProgram?.id) {
                this.faxFormData.programID = this.selectedProgram.id;
            }
        } else {
            this.faxFormData.rxID = this.prescription.rxID;
            this.faxFormData.storeID = Number(this.store.id);
            this.faxFormData.appendPrescriptionImage = this.appendPrescriptionImage;
            this.faxFormData.excludeRefillRequestsFromNotes = this.excludeRefillRequestsFromNotes;
        }
    }

    // The C# PrescriberFollowUpCase.FaxType name expected by the fax-preview route.
    get previewFaxTypeName(): string {
        if (this.customTemplate) return "CustomPrescriberTemplate";

        switch (this.formType) {
            case FaxType.RefillAuthorization: return "RefillAuthorization";
            case FaxType.RxClarification: return "RxClarification";
            case FaxType.NewRxRequest: return "NewRxRequest";
            case FaxType.AlternateRxRequest: return "AlternateRx";
            default: return "";
        }
    }

    get canPreview(): boolean {
        const validSelectedFields = (this.formType == FaxType.NewRxRequest
            && !!this.selectedPrescriber.id && !!this.selectedDrug.drugId && !!this.storeFilter.id)
            || this.formType != FaxType.NewRxRequest;
        return validSelectedFields && !this.loadingPrograms && !this.isSending;
    }

    // Renders the exact PDF that would be transmitted without sending it or creating a follow-up.
    preview() {
        const faxTypeName = this.previewFaxTypeName;
        if (!faxTypeName) return;

        this.populateFaxFormData();
        this.previewModal.open(async () => {
            const res = await this.$http.post(`prescription/fax-preview/${faxTypeName}`, this.faxFormData, {
                params: this.customTemplate ? { templateId: this.customTemplate.id } : undefined,
                responseType: "blob",
            });
            return res.data;
        });
    }

    async send() {
        this.populateFaxFormData();

        const force = this.customTemplate ? false : await this.confirmIfDuplicateExists();
        if (force === null) return;

        try {
            this.isSending = true;

            const res = await this.$http.post<PatientDocument>(`prescription/${this.endpointURI}`,
                this.faxFormData,
                { params: force ? { force: true } : undefined });

            if (!res.data.id) return;

            let msg = `${this.title} sent successfully and saved as a new patient document. A new Follow-Up case was created.`;

            const canSendClarificationFax = this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Pharmacists/Fax Rx Clarification Request".toLowerCase());

            if (!canSendClarificationFax && this.formType == FaxType.RxClarification) {
                msg = `${this.title} was saved as a new patient document and new Follow-Up case was created for review.`;
            }

            if (this.customTemplate) {
                msg = `${this.customTemplate.name} sent successfully and saved as a new patient document.`;
            }

            this.$notification(
                NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success)
            );
            this.clearForm();
            this.$emit('saved');
            this.$bvModal.hide(this.modalId);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            const errorMsg = `Error while sending ${this.title}.`;
            console.error(errorMsg, err);
        } finally {
            this.isSending = false;
        }
    }

    async confirmIfDuplicateExists(): Promise<boolean | null> {
        const payload = {
            type: this.formType,
            storeId: this.faxFormData.storeID || null,
            rxNumber: this.prescription?.rxNumber ?? null,
            rfNumber: this.prescription?.rfNumber ?? null,
            patientId: this.faxFormData.patientID || null,
            prescriberId: this.faxFormData.prescriberID || this.prescriberToShow?.id || null,
            packageID: this.faxFormData.packageID || null,
        };

        try {
            const res = await this.$http.post<PrescriberFollowUpCase | null>(
                '/Prescription/check-duplicate-follow-up', payload);
            if (!res?.data?.id) return false;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `A ${this.title} already exists for this Rx. Send another fax and open a new follow-up?`,
                {
                    title: 'Duplicate request',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'warning',
                    okTitle: 'Continue',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true,
                });
            return confirmed ? true : null;
        } catch (err) {
            console.error('Duplicate follow-up pre-check failed', err);
            this.$notification(NotificationOptions.notificationOptionsPreset(
                `Could not check for duplicate ${this.title} requests. Confirm before sending.`,
                NotificationOptions.NotificationTypes.warning));
            const proceed = await this.$bvModal.msgBoxConfirm(
                `The duplicate-request check did not run. Send this ${this.title} anyway?`,
                {
                    title: 'Duplicate check unavailable',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'warning',
                    okTitle: 'Send anyway',
                    cancelTitle: 'Cancel',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true,
                });
            return proceed ? false : null;
        }
    }

    async getConfig() {
        try {
            this.excludeRefillRequestsFromNotes = (await this.$http.get<boolean>("/configuration/ExcludeRefillRequestsFromNotes")).data;
        } catch (error) {
            console.error("Failed to load configuration:", error);
            this.excludeRefillRequestsFromNotes = false; // Safe default
        }
    }

    close() {
        this.$bvModal.hide(this.modalId);
        this.selectedDrug = new Drug();
        this.selectedPrescriber = new Prescriber();
        this.selectedProgram = new Program();
    }

    clearForm() {
        this.faxFormData = new FaxForm();
        this.selectedProgram = new Program();
    }

    get isPortalContact() {
        return this.prescriberToShow.communicationPreference === PrescriberCommunicationPreference.portal;
    }

    get isFormValid() {
        const validSelectedFields = this.formType == FaxType.NewRxRequest && !!this.selectedPrescriber.id && !!this.selectedDrug.drugId && !!this.storeFilter.id
            || this.formType != FaxType.NewRxRequest;

        const hasFaxOrPortal = this.isPortalContact || !!this.prescriberToShow.faxNumber;
        return hasFaxOrPortal && validSelectedFields && !this.loadingPrograms;
    }

    get showInvalidFormAlert() {
        return !!this.prescriberToShow.id && !this.prescriberToShow.faxNumber && !this.isPortalContact;
    }

    mounted() {
        this.storeFilter = new Store(this.lastStore) ?? new Store();
    }
}
</script>

<style scoped></style>
